import { rerenderCartContent } from '../services/cart';
import { onKeyUpEscape, removeTrapFocus, trapFocus } from '../utils/dom';
import { debounce } from '../utils/performance';
import { renderRecommendedProducts } from './cart-drawer';

const FETCH_CONFIG = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

class CartCloseBtn extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => this.closest('cart-drawer').close());
  }
}

customElements.define('cart-close-button', CartCloseBtn);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const itemIndex = this.dataset.index;
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');

      cartItems.updateQuantity(itemIndex, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  onUpdateCart(parsedState, line, quantity, name) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) {
      cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    if (cartDrawerWrapper) {
      cartDrawerWrapper.classList.toggle(
        'is-empty',
        parsedState.item_count === 0
      );
    }

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      if (parsedState.sections) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });

    this.updateLiveRegions(line, parsedState.item_count);

    const lineItem =
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`);

    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      cartDrawerWrapper
        ? trapFocus(
            cartDrawerWrapper,
            lineItem.querySelector(`[name="${name}"]`)
          )
        : lineItem.querySelector(`[name="${name}"]`).focus();
    } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
      trapFocus(
        cartDrawerWrapper.querySelector('.drawer__inner-empty'),
        cartDrawerWrapper.querySelector('a')
      );
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
    }

    this.disableLoading();

    if (parsedState.item_count === +quantity) {
      document.dispatchEvent(
        new CustomEvent('cart:quantity:changed', {
          detail: { name, line, quantity },
        })
      );
    }

    rerenderCartContent();

    document.dispatchEvent(
      new CustomEvent('cart:change', { detail: { parsedState } })
    );
  }

  onQuantityUpdateError(er) {
    console.log('Error updating cart item quantity', er);
    this.querySelectorAll('.loading-overlay').forEach((overlay) =>
      overlay.classList.add('hidden')
    );
    const errors =
      document.getElementById('cart-errors') ||
      document.getElementById('CartDrawer-CartErrors');
    errors.textContent = window.cartStrings.error;
    this.disableLoading();
  }

  replaceQtyWithNotDiscountItems(bundleKey, discountedItems) {
    const items = Array.from(
      this.querySelectorAll(
        `[data-parent-bundle-id="${bundleKey}"]:not([data-is-discounted])`
      )
    );
    const qtyRecord = items.reduce((acc, item) => {
      const discountedItem = discountedItems.find(
        (dItem) => dItem.dataset.variantId === item.dataset.variantId
      );
      const quantity = +item.querySelector('.quantity__input').value;
      acc[discountedItem.dataset.lineKey] = quantity;
      return acc;
    }, {});

    return items.length ? qtyRecord : null;
  }

  makeCartUpdate(cartChangeUrl, body, line, quantity, name, noUIUpdate) {
    return fetch(cartChangeUrl, { ...FETCH_CONFIG, body: JSON.stringify(body) })
      .then((response) => response.json())
      .then(
        (state) => !noUIUpdate && this.onUpdateCart(state, line, quantity, name)
      )
      .catch(this.onQuantityUpdateError.bind(this));
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sections = this.getSectionsToRender().map(({ section }) => section);
    const sections_url = window.location.pathname;
    let cartChangeUrl = window.routes.cart_change_url;

    let body = {
      line,
      quantity: +quantity,
      sections,
      sections_url: window.location.pathname,
    };
    let notDiscountedItemsQty;

    await this.makeCartUpdate(
      cartChangeUrl,
      body,
      line,
      quantity,
      name,
      Boolean(notDiscountedItemsQty)
    );
    if (notDiscountedItemsQty) {
      await this.makeCartUpdate(
        cartChangeUrl,
        {
          updates: notDiscountedItemsQty,
        },
        line,
        quantity,
        name
      );
    }
    renderRecommendedProducts();
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement =
        document.getElementById(`Quantity-${line}`) ||
        document.getElementById(`Drawer-quantity-${line}`);

      lineItemError.querySelector('.cart-item__error-text').innerHTML =
        window.cartStrings.quantityError.replace(
          '[quantity]',
          quantityElement.value
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...FETCH_CONFIG,
              ...{ body },
            });
          }, 300)
        );
      }
    }
  );
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    document.addEventListener('cart:change', (e) => this.open(false, true));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, cartQuantityChange) {
    if (document.body.dataset.templateName === 'cart') {
      if (cartQuantityChange) return;
      window.location = '/cart';
      return;
    }
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    setTimeout(() => {
      document.querySelector('.drawer').classList.remove('active', 'animate');
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    renderRecommendedProducts();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class componentsToggler extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      const lineItem = e.target.closest('.cart__items--row');
      const isExpanded = lineItem?.dataset.expanded === 'true';
      lineItem?.setAttribute('data-expanded', isExpanded ? 'false' : 'true');
    });
  }
}

customElements.define('components-toggler', componentsToggler);

window.refreshCart = rerenderCartContent;